import React, { useCallback, useMemo } from 'react';
import _ from 'lodash';
import {
  TextAreaFormField, InputFormField, Toggle, ContainerToggle, TextToggle,
} from '@playbookux/playbook-web-core';
import { Field, FieldArray } from 'formik';
import { v4 } from 'uuid';
import SelectFormField from '../../../../../core/components/select-form-field';

import {
  screenerSingle,
  screenerMultiple,
} from '../options';

import {
  AddScreenerQuestion,
  AnswersContainer,
  HeaderSegmentContainer,
  IconCircle,
  InputAnswerContainer,
  QualifyContainer,
  QuestionField,
  QuestionSegment,
  ScreenerQuestionContainer,
  SegmentTitle,
  SetUpContainer,
  Radio,
  ActionContainer,
  QuestionOptionPercentage,
  ColoredQuestionRating,
  ActionButton,
} from '../styles';

const HandleQuestions = (questions, formErrors, formPrefix, setFieldValue, setFieldTouched) => {
  const handleRemoveQuestion = useCallback((question) => {
    const updatedQuestions = _.pull(questions, question);
    const updatedQuestionsArray = [...updatedQuestions];
    setFieldValue('questions', updatedQuestionsArray);
  }, [questions, setFieldValue]);

  const handleDuplicateQuestion = useCallback((questionIndex) => {
    let valuesClone = _.cloneDeep(questions);

    const duplicatedQuestion = _.cloneDeep(valuesClone[questionIndex]);
    duplicatedQuestion.id = v4();
    // in this flow we set the duplicated question's index to the next index instead of the last index
    duplicatedQuestion.index = questionIndex + 1;
    duplicatedQuestion.answers = duplicatedQuestion.answers.map((val) => ({ ...val, id: v4(), optionRatingPercentage: '' }));
    duplicatedQuestion.screenerRatingPercentage = '';

    valuesClone.splice(questionIndex + 1, 0, duplicatedQuestion);

    // we also need update the index of all questions after duplicating
    valuesClone = valuesClone.map((question, index) => ({
      ...question,
      index,
    }));

    setFieldValue('questions', valuesClone);
  }, [questions, setFieldValue]);

  const handleAddAnswer = (question, arrayHelper, arr) => {
    const defaultType = question.type === 'single_select' ? 'Qualify' : 'May Select';
    const newAnswer = {
      id: v4(), answer: '', type: defaultType, optionType: 'answer',
    };
    const lastOption = _.last(arr);

    // insert before if last item is Other type
    if (lastOption?.optionType && lastOption?.optionType === 'other') return arrayHelper.insert(arr.length - 1, newAnswer);

    return arrayHelper.push(newAnswer);
  };

  const handleRemoveAnswer = (arrayHelpers, indexAnswer, answers) => {
    if (answers.length > 2) {
      arrayHelpers.remove(indexAnswer);
    }
  };

  /* Handle selection type changes */

  const handleAnswerOnSelectionChange = (answers) => (
    answers.map((answer) => (answer.type === 'May Select' ? { ...answer, type: 'Qualify' } : answer))
  );

  const mapMaySelectAnswers = (answers) => (
    answers.map((answer) => (answer.type !== 'May Select' ? { ...answer, type: 'May Select' } : answer))
  );

  const handleSelectionTypeChange = (questionIndex, value) => {
    const updatedQuestions = _.cloneDeep(questions);
    const question = updatedQuestions[questionIndex];
    question.type = value;
    if (value === 'single_select') {
      question.answers = handleAnswerOnSelectionChange(question.answers);
    } else if (value === 'multiple_select') {
      question.answers = mapMaySelectAnswers(question.answers);
    }

    setFieldValue('questions', updatedQuestions);
  };

  const toggleRandomize = (question, questionIndex) => {
    const fieldPath = `questions[${questionIndex}].randomize`;
    setFieldValue(fieldPath, !question.randomize);
  };

  const renderQuestions = useMemo(() => (
    questions?.map((question, indexQuestion) => {
      const isOnlyQuestion = questions.length === 1;
      const questionsError = formErrors.questions || [];
      const { answers: answersErrors = [], question: textQuestionError } = questionsError[indexQuestion] || {};

      return (
        <QuestionSegment key={question.id}>
          <HeaderSegmentContainer>
            <SegmentTitle>{`Question ${indexQuestion + 1}`}</SegmentTitle>

            <ActionContainer>
              <ActionButton
                onClick={() => handleDuplicateQuestion(indexQuestion)}
              >
                Duplicate
              </ActionButton>

              <ActionButton
                disabled={isOnlyQuestion}
                onClick={() => handleRemoveQuestion(question)}
              >
                Delete
              </ActionButton>
            </ActionContainer>

            <ColoredQuestionRating percentage={question.screenerRatingPercentage ?? null}>
              {question.screenerRatingPercentage
                ? `${question.screenerRatingPercentage} of participants qualified`
                : 'Pass rates unavailable'}
            </ColoredQuestionRating>
          </HeaderSegmentContainer>

          <QuestionField>
            <Field
              placeholder="Type your screener question here"
              name={`${formPrefix}questions.${indexQuestion}.question`}
              error={textQuestionError}
              component={TextAreaFormField}
              audience
              minWidth="100%"
              // this is necessary to enable visual validation when user types for the first time,
              // because the field is not touched yet, so it does not show the visual error
              // and onChange by itself does not update the touched state, only onBlur
              onChange={(e) => {
                setFieldValue(`${formPrefix}questions.${indexQuestion}.question`, e.target.value);
                setFieldTouched(`${formPrefix}questions.${indexQuestion}.question`, true);
              }}
            />
          </QuestionField>

          <FieldArray
            name={`${formPrefix}questions.${indexQuestion}.answers`}
            render={(arrayHelpers) => (
              <>
                {questions[indexQuestion].answers?.map((answer, indexAnswer) => (
                  <AnswersContainer key={answer.id}>
                    <InputAnswerContainer>
                      <Field
                        name={`${formPrefix}questions.${indexQuestion}.answers.${indexAnswer}.answer`}
                        error={answersErrors[indexAnswer] && answersErrors[indexAnswer].answer}
                        placeholder="Type your answer here"
                        readOnly={
                          questions[indexQuestion].answers[indexAnswer].optionType === 'other'
                        }
                        component={InputFormField}
                        minWidth="325px"
                        audience
                        onChange={(e) => {
                          setFieldValue(`${formPrefix}questions.${indexQuestion}.answers.${indexAnswer}.answer`, e.target.value);
                          setFieldTouched(`${formPrefix}questions.${indexQuestion}.answers.${indexAnswer}.answer`, true);
                        }}
                      />
                    </InputAnswerContainer>
                    <QualifyContainer>
                      <Field
                        name={`${formPrefix}questions.${indexQuestion}.answers.${indexAnswer}.type`}
                        options={questions[indexQuestion].type === 'single_select'
                          ? screenerSingle : screenerMultiple}
                        component={SelectFormField}
                        audience
                      />
                    </QualifyContainer>

                    <IconCircle
                      onClick={() => handleRemoveAnswer(arrayHelpers, indexAnswer, question.answers)}
                      src="/images/remove.png"
                      alt="Remove"
                      disabled={question.answers.length <= 2}
                    />

                    <QuestionOptionPercentage>
                      {answer.optionRatingPercentage}
                    </QuestionOptionPercentage>
                  </AnswersContainer>
                ))}

                <ScreenerQuestionContainer>
                  <AddScreenerQuestion
                    onClick={() => handleAddAnswer(question, arrayHelpers, question.answers)}
                  >
                    + Answer
                  </AddScreenerQuestion>
                </ScreenerQuestionContainer>

                {_.isEmpty(_.pickBy(
                  questions[indexQuestion].answers,
                  (answer) => answer.optionType === 'other',
                ))

                  && (
                    <>
                      <AddScreenerQuestion
                        onClick={() => arrayHelpers.push({
                          id: v4(), answer: 'Other', type: 'Qualify', optionType: 'other',
                        })}
                      >
                        + &#39;Other&#39; as an answer choice
                      </AddScreenerQuestion>
                    </>
                  )}

                <ContainerToggle marginVertical>
                  <Toggle
                    onChange={() => toggleRandomize(question, indexQuestion)}
                    checked={question.randomize}
                    id={`multiple-select-id-${indexQuestion}`}
                    name="randomize"
                  />

                  <TextToggle fontSize={12}>Randomize answer choices</TextToggle>
                </ContainerToggle>

                <SetUpContainer>
                  <SegmentTitle>
                    Multiple choice options
                  </SegmentTitle>

                  <Radio
                    value={question.type}
                    name={`Single Select-Q${question}-${v4()}`}
                    label="Single select - participants can select one option"
                    checked={question.type === 'single_select'}
                    onClick={() => handleSelectionTypeChange(indexQuestion, 'single_select')}
                  />

                  <Radio
                    value={question.type}
                    name={`Multiple Select-Q${question}-${v4()}`}
                    label="Multiple select - participants can select one or more options"
                    checked={question.type === 'multiple_select'}
                    onClick={() => handleSelectionTypeChange(indexQuestion, 'multiple_select')}
                  />
                </SetUpContainer>
              </>
            )}
          />
        </QuestionSegment>
      );
    })
  ), [questions, formErrors, formPrefix, handleRemoveQuestion, handleDuplicateQuestion, handleSelectionTypeChange]);

  return renderQuestions;
};

export default HandleQuestions;
